import React, { useState } from 'react';
import styled from 'styled-components';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import {
  Dialog,
  Button,
  CircularProgress,
  Box,
  Typography,
  Alert,
} from '@mui/material';

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLoginSuccess: () => void;
}

const LoginContainer = styled.div`
  padding: 2rem;
  max-width: 400px;
  width: 100%;
  text-align: center;
`;

const LoginHeader = styled.div`
  margin-bottom: 1.5rem;
`;

const LoginTitle = styled.h2`
  font-size: 1.8rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 0.5rem 0;
`;

const LoginSubtitle = styled.p`
  font-size: 0.95rem;
  color: #64748b;
  margin: 0;
  line-height: 1.4;
`;

const LoginButton = styled(Button)`
  width: 100%;
  font-size: 1rem;
  padding: 0.875rem 1.5rem;
  margin-bottom: 1rem;
  text-transform: none;
  font-weight: 600;
  border-radius: 8px;
`;

const LoginModal: React.FC<LoginModalProps> = ({ isOpen, onClose, onLoginSuccess }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const { login } = useAuth();
  const { showError } = useToast();

  const handle_login_redirect = async () => {
    setError('');
    setIsLoading(true);

    try {
      await login();
      onLoginSuccess();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Login failed';
      setError(errorMessage);
      showError(errorMessage);
      setIsLoading(false);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxWidth: 400,
        },
      }}
    >
      <LoginContainer>
        <LoginHeader>
          <LoginTitle>ログイン</LoginTitle>
          <LoginSubtitle>
          このツールはTFACTを経由してログインします。
          </LoginSubtitle>
        </LoginHeader>

        {isLoading && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              marginY: 2,
            }}
          >
            <CircularProgress size={24} />
            <Typography variant="body2" sx={{ marginLeft: 2 }}>
              リダイレクト中...
            </Typography>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <LoginButton
            variant="contained"
            onClick={handle_login_redirect}
            disabled={isLoading}
            sx={{
              backgroundColor: '#7c3aed',
              '&:hover': {
                backgroundColor: '#6d28d9',
              },
              marginTop: 1,
            }}
          >
            TFACTでログイン
          </LoginButton>
        </Box>
      </LoginContainer>
    </Dialog>
  );
};

export default LoginModal;